// src/contexts/AriaContext.js
// Author: Sunil Jain
// Description: Context provider for managing AI Assistant (Aria) state and deep patient context.

import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';

const AriaContext = createContext();

export const useAriaContext = () => useContext(AriaContext);

export const AriaContextProvider = ({ children }) => {
    const { authFetch, user } = useAuth();
    const [isContextLoading, setIsContextLoading] = useState(false);
    const [deepPatientContext, setDeepPatientContext] = useState(null);
    const [contextError, setContextError] = useState(null);

    // Function to fetch the "Deep Context" (RAG summary) for the current patient
    const fetchDeepContext = useCallback(async () => {
        if (!user || !user.id) return;

        setIsContextLoading(true);
        setContextError(null);
        try {
            // Correct API endpoint for patient-facing context
            const data = await authFetch(`/api/patients/${user.id}/deep_context`);
            if (data && !data.error) {
                setDeepPatientContext(data);
                console.log("AriaContext: Successfully loaded deep patient context.");
            } else {
                console.warn("AriaContext: Deep context response invalid or empty.", data);
                setDeepPatientContext(null);
            }
        } catch (error) {
            console.error("AriaContext: Failed to load deep context.", error);
            setContextError(error.message);
            setDeepPatientContext(null);
        } finally {
            setIsContextLoading(false);
        }
    }, [authFetch, user]);

    // Automatically fetch context when the user logs in
    useEffect(() => {
        if (user?.id) {
            fetchDeepContext();
        } else {
            setDeepPatientContext(null);
        }
    }, [user?.id, fetchDeepContext]);

    const clearDeepPatientContext = useCallback(() => {
        setDeepPatientContext(null);
        setContextError(null);
    }, []);

    const value = {
        deepPatientContext,
        setDeepPatientContext,
        isContextLoading,
        setIsContextLoading,
        contextError,
        fetchDeepContext,
        clearDeepPatientContext
    };

    return (
        <AriaContext.Provider value={value}>
            {children}
        </AriaContext.Provider>
    );
};

export default AriaContext;